import { useState, useEffect, useContext, useRef } from 'react';
import { searchWord, getSuggestions, DictionaryResponse } from '../services/dictionary';
import SearchBar from '../components/SearchBar';
import WordDefinition from '../components/WordDefinition';
import { UserContext } from '../store/useStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Sparkles, Clock, Plus } from 'lucide-react';
import { translateFallback, translateText } from '../hooks/useTranslation';
import { AuthContext } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import WordCard from '../components/WordCard';
import ExpressionForm from '../components/ExpressionForm';
import { localIdioms } from '../data/expressions';
import WordOfTheDayCard from '../components/WordOfTheDayCard';
import { db } from '../lib/firebase';
import { ref, push, serverTimestamp } from 'firebase/database';

export default function Expressions() {
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wordData, setWordData] = useState<DictionaryResponse | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [translatedWord, setTranslatedWord] = useState<string | null>(null);
  const [loginModal, setLoginModal] = useState(false);
  const [wordSaved, setWordSaved] = useState(false);
  const [showExpressionForm, setShowExpressionForm] = useState(false);
  const [failedWord, setFailedWord] = useState('');
  const [textToTranslate, setTextToTranslate] = useState<string>('');
  const { savedExpressions, getExpressionsPaginated, loadMoreExpressions, loadingExpressions, addExpression } = useContext(UserContext)
  const { currentUser } = useContext(AuthContext)
  const { state } = useLocation()
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsSearching(false);
  };

  const handleSearch = async (word: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsSearching(true);
    setError(null);
    setWordData(null);
    setShowExpressionForm(false);
    try {
      const trimmedWord = word.trim();
      const data = await searchWord(trimmedWord, abortController.signal);
      
      if (abortController.signal.aborted) return;

      setWordData(data);
      console.log('Expression data:', data);
      setWordSaved(false);
      
      if (data.translation) {
        const formattedTranslation = data.translation.charAt(0).toUpperCase() + data.translation.slice(1);
        setTranslatedWord(formattedTranslation);
      } else {
        const firstMeaning = data.meanings[0];
        let currentTextToTranslate = trimmedWord;
        if (firstMeaning && firstMeaning.definitions && firstMeaning.definitions.length > 0) {
          const firstDef = firstMeaning.definitions[0];
          currentTextToTranslate = typeof firstDef === 'string' ? firstDef : firstDef.definition;
        }
        setTextToTranslate(currentTextToTranslate);
        translate(currentTextToTranslate);
      }
    } catch (err: unknown) {
      if ((err as Error)?.name === 'AbortError') {
        console.log('Search aborted');
        return;
      }
      setFailedWord(word.trim());
      setShowExpressionForm(true);
      setError(`Expression "${word.trim()}" not found. You can add its meaning manually below.`);
      console.error('Error searching expression:', err);
    } finally {
      if (abortControllerRef.current === abortController && !abortController.signal.aborted) {
        setIsSearching(false);
      }
    }
  };

  const handleInputChange = async (word: string) => {
    // Basic suggestion logic (might not work well for long expressions, but kept for consistency)
    if (word.length > 1) {
      const newSuggestions = await getSuggestions(word);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (word: string) => {
    handleSearch(word);
  };

  const handleSave = async () => {
    if(!currentUser) {
      setLoginModal(true);
      return;
    }
    if (!wordData) return;

    setIsSaving(true);
    setError(null);
    
    try {
      addExpression(wordData)
      setError('Expression saved successfully!');
      setWordSaved(true)
    } catch (err) {
      console.error('Error saving expression:', err);
      setError('Failed to save expression. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const translate = async (text: string) => {
    setTranslatedWord(null)
    const translationResult = await translateText(text, 'en', 'pt');
    if (translationResult.toLowerCase().includes(text.toLowerCase())) {
      translateFallback(text).then((result) => {
        setTranslatedWord(result);
      });
    } else {
      setTranslatedWord(translationResult);
    }
  }

  const handleFlagTranslation = async () => {
    if (!wordData || !translatedWord) return;
    
    // Save to Firebase Realtime Database
    try {
      const flaggedRef = ref(db, 'flagged_translations');
      await push(flaggedRef, {
        word: wordData.word,
        textToTranslate: textToTranslate,
        wrongTranslation: translatedWord,
        timestamp: serverTimestamp(),
        userId: currentUser?.uid || 'anonymous'
      });
      console.log('Flagged translation saved to database');
    } catch (err) {
      console.error('Error saving flagged translation:', err);
    }
    
    // Attempt fallback translation
    try {
      setTranslatedWord('Loading fallback translation...');
      const fallbackResult = await translateFallback(textToTranslate);
      if (fallbackResult) {
        setTranslatedWord(fallbackResult);
      } else {
        setTranslatedWord(translatedWord); // Restore if empty
      }
    } catch (err) {
      console.error('Error fetching fallback translation:', err);
      setTranslatedWord(translatedWord); // Restore on error
    }
  };

  useEffect(() => {
    if (state && state.word) {
      handleSearch(state.word)
    }
  }, [state])

  // Get top 10 recent saved expressions
  const getRecentSavedExpressions = () => {
    if (!currentUser || savedExpressions.length === 0) return [];
    
    return [...savedExpressions]
      .sort((a, b) => {
        const aDate = a.lastPractice || a.createdAt || 0;
        const bDate = b.lastPractice || b.createdAt || 0;
        return bDate - aDate; // Most recent first
      })
      .slice(0, 10)
      .map(exp => exp.word);
  };

  const recentSavedExpressions = getRecentSavedExpressions();
  
  // States for local database filtering
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  
  const categories = ['All', ...Array.from(new Set(localIdioms.map(i => i.category)))];
  
  const filteredIdioms = selectedCategory === 'All' 
    ? localIdioms 
    : localIdioms.filter(i => i.category === selectedCategory);

  const trainedExpressionsCount = savedExpressions.filter(e => (e.progress || 0) > 0).length;

  const DatabaseSection = () => (
    <div className="mt-8">
      {/* Stats Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-indigo-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-indigo-50 rounded-lg"><Sparkles className="w-6 h-6 text-indigo-600" /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Database</p>
            <p className="text-2xl font-bold text-gray-800">{localIdioms.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 rounded-lg"><MessageCircle className="w-6 h-6 text-emerald-600" /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Saved Idioms</p>
            <p className="text-2xl font-bold text-gray-800">{savedExpressions.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-yellow-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-yellow-50 rounded-lg"><Clock className="w-6 h-6 text-yellow-600" /></div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Trained Idioms</p>
            <p className="text-2xl font-bold text-gray-800">{trainedExpressionsCount}</p>
          </div>
        </div>
      </div>

      {/* Recent Saved Expressions */}
      {currentUser && recentSavedExpressions.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-800">Your Recent Expressions</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentSavedExpressions.map((word) => (
              <button
                key={word}
                onClick={() => handleSearch(word)}
                className="px-4 py-2 bg-white border border-blue-200 rounded-full text-sm font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm hover:shadow-md"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Database Grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-800">Browse Expressions</h2>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setWordData(null);
                setFailedWord('');
                setShowExpressionForm(true);
                setError(null);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg text-sm font-medium transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Custom Expression
            </button>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === cat ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredIdioms.map((idiom) => {
            const isSaved = savedExpressions.some(e => e.word.toLowerCase() === idiom.phrase.toLowerCase());
            return (
              <div 
                key={idiom.id}
                onClick={() => handleSearch(idiom.phrase)}
                className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-indigo-300 hover:shadow-lg transition-all group relative flex flex-col h-full"
              >
                {isSaved && (
                  <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]" title="Saved"></div>
                )}
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-500 mb-2">{idiom.category}</span>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">{idiom.phrase}</h3>
                <p className="text-sm text-gray-600 italic mt-auto">{idiom.meaning}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-indigo-50 to-white pb-16'>
      <Modal 
          isOpen={loginModal} 
          setIsModalOpen={setLoginModal} 
          title='Login or a Create Account'
          message='To save an expression you must be logged in!'
          actionText='Login/Register' 
          action={() => navigate('/login')}
          />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
        <header className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <MessageCircle className="w-12 h-12 text-indigo-600" />
              <h1 className="text-4xl font-bold text-gray-900">Expressions & Idioms</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Search for slangs, phrasal verbs, and common sentences!
            </p>
          </header>
          <SearchBar 
            onSearch={handleSearch} 
            isLoading={isSearching}
            suggestions={suggestions}
            onSuggestionSelect={handleSuggestionSelect}
            onInputChange={handleInputChange}
            wordParam={state?.word}
            onCancel={handleCancelSearch}
          />
          
          {/* User Feedback Alert - Positioned prominently below search bar */}
          {error && (
            <div className={`mt-4 p-4 rounded-lg flex items-center justify-between gap-2 ${error.includes('successfully!') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              <span>{error}</span>
              {showExpressionForm && (
                <button
                  type="button"
                  onClick={() => setShowExpressionForm(false)}
                  className="text-xs font-semibold underline hover:no-underline ml-auto"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}

          {/* Manual Expression Form - Positioned directly below SearchBar */}
          {showExpressionForm && (
            <div className="mt-4">
              <ExpressionForm
                key={failedWord}
                initialWord={failedWord}
                isSaving={isSaving}
                onCancel={() => {
                  setShowExpressionForm(false);
                  setError(null);
                }}
                onSave={async (customData) => {
                  if (!currentUser) {
                    setLoginModal(true);
                    return;
                  }
                  setIsSaving(true);
                  try {
                    await addExpression(customData);
                    setWordData(customData);
                    setWordSaved(true);
                    setShowExpressionForm(false);
                    setError('Expression saved successfully!');
                    if (customData.translation) {
                      setTranslatedWord(customData.translation);
                    }
                  } catch {
                    setError('Failed to save expression. Please try again.');
                  } finally {
                    setIsSaving(false);
                  }
                }}
              />
            </div>
          )}

          {/* Database Section - Show when no word is displayed and form is closed */}
          {!wordData && !showExpressionForm && <DatabaseSection />}
        </div>

        {wordData && (
          <>
            <WordDefinition 
              data={wordData} 
              onSave={handleSave}
              isSaving={isSaving}
              translated={translatedWord!}
              wordSaved={wordSaved}
              setWordSaved={setWordSaved}
              themeName={state?.themeName}
              setWordData={setWordData}
              onFlagTranslation={handleFlagTranslation}
              isExpression={true}
            />
            <WordOfTheDayCard word={wordData.word} hideHeader={true} />
            {/* Database Section - Show after word definition */}
            <DatabaseSection />
          </>
        )}

        {currentUser && savedExpressions.length > 0 && wordData === null && (<div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 mt-6">Saved Expressions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedExpressions.map((word) => (
                    <WordCard
                        key={word.word}
                        word={word.word}
                        lastPractice={word.lastPractice || word.createdAt}
                        progress={word.progress}
                        showDelete={true}
                        isExpression={true}
                    />
                ))}
            </div>
            {loadMoreExpressions && savedExpressions.length > 0 && (
                <button
                    onClick={() => getExpressionsPaginated(true)}
                    disabled={loadingExpressions}
                    className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                    {loadingExpressions ? "Loading..." : "Load More"}
                </button>
            )}
        </div>)}
      </div>
    </div>
  );
}
